import React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useMeQuery } from "@/queries/queries";

const PORTAL_RETURN_FLAG_KEY = "portalReturnMode";
const PORTAL_RETURN_BUILDING_KEY = "portalReturnBuildingId";

const readStoredFlag = (): boolean => {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(PORTAL_RETURN_FLAG_KEY) === "1";
};

export const usePortalReturnMode = (): boolean => {
  // Hook order pin: useMeQuery, useLocation, and useState/useEffect MUST
  // be called unconditionally and in this order on every render so the
  // hook count stays stable across loading → loaded → logged-out cycles.
  // Don't move any of these below an early return.
  const { data: me } = useMeQuery();
  const location = useLocation();
  // URL search wins so that arriving via `?fromPortal=1` activates the bar
  // immediately. We then mirror to sessionStorage so subsequent navigations
  // within Classic (which TanStack `<Link>` does NOT propagate search params
  // through) keep the bar visible until the user explicitly clicks back.
  // TanStack Router exposes `location.search` as a parsed object.
  const search = (location.search ?? {}) as Record<string, unknown>;
  const fromUrl = search.fromPortal === "1" || search.fromPortal === 1;
  const [stored, setStored] = React.useState<boolean>(readStoredFlag);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (fromUrl) {
      sessionStorage.setItem(PORTAL_RETURN_FLAG_KEY, "1");
      const m = location.pathname.match(/^\/buildings\/([^/]+)/);
      if (m) sessionStorage.setItem(PORTAL_RETURN_BUILDING_KEY, m[1]);
      setStored(true);
    }
  }, [fromUrl, location.pathname]);

  // Wave 2: any user whose Company has been flipped to platformHome="portal"
  // should see the minimal "Back to Tracker" shell automatically when they
  // land on a Classic surface — even without `?fromPortal=1`. This covers:
  //   - future flipped customers who bookmark a Classic URL.
  //
  // Staff exception (2026-05-15): staff users are platform admins, not
  // customers using a flow — when they navigate to a Classic surface
  // they expect the FULL Filing-app shell (sidebar, header, all of it),
  // not the trimmed "Back to Tracker" mode. The "Filing View" menu item
  // in TrackerSidebar relies on this exception to land staff into the
  // real Filing app rather than the sidebar-less return shell.
  const isOnClassicSurface = !location.pathname.startsWith("/portal");
  const inheritedFromCompany =
    me?.company?.platformHome === "portal"
    && isOnClassicSurface
    && !me?.isStaff;

  return fromUrl || stored || inheritedFromCompany;
};

const inferReturnTarget = (pathname: string): string => {
  const m = pathname.match(/^\/buildings\/([^/]+)/);
  if (m) return `/portal/buildings/${m[1]}`;
  if (typeof window !== "undefined") {
    const stored = sessionStorage.getItem(PORTAL_RETURN_BUILDING_KEY);
    if (stored) return `/portal/buildings/${stored}`;
  }
  return "/portal/";
};

const clearReturnState = () => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(PORTAL_RETURN_FLAG_KEY);
  sessionStorage.removeItem(PORTAL_RETURN_BUILDING_KEY);
};

const PortalReturnBar: React.FC = () => {
  const location = useLocation();
  const target = inferReturnTarget(location.pathname);

  return (
    <div
      className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur"
      role="banner"
      aria-label="Tracker return bar"
    >
      <div className="flex items-center px-3 py-2">
        <Link
          to={target}
          onClick={clearReturnState}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-foreground motion-tint hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="portal-return-bar-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tracker
        </Link>
      </div>
    </div>
  );
};

export default PortalReturnBar;
